/**
 * Settings del Refinery advisor — viven en localStorage del navegador del user.
 *
 * Como Tax.tsx, scope MVP personal: el user pone sus skills / implant /
 * station bonus / refining tax. Cuando venga la fase corp, liftamos a una
 * tabla D1 y este helper cambia a leer del API — el resto del código no se
 * toca.
 */

const KEY = 'eve.refinery.settings.v1'

export type ImplantPct = 0 | 4 | 6 | 10
export type StationBonusPct = 0 | 1 | 3 | 6
export type RefiningTaxPct = 0 | 2 | 5

export type RefinerySettings = {
  skill_reprocess: 0 | 1 | 2 | 3 | 4 | 5
  skill_reprocess_efficiency: 0 | 1 | 2 | 3 | 4 | 5
  implant_pct: ImplantPct
  station_bonus_pct: StationBonusPct
  refining_tax_pct: RefiningTaxPct
}

export const DEFAULT_REFINERY_SETTINGS: RefinerySettings = {
  skill_reprocess: 0,
  skill_reprocess_efficiency: 0,
  implant_pct: 0,
  station_bonus_pct: 0,
  refining_tax_pct: 0,
}

export function loadRefinerySettings(): RefinerySettings {
  if (typeof localStorage === 'undefined') return DEFAULT_REFINERY_SETTINGS
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return DEFAULT_REFINERY_SETTINGS
    const parsed = JSON.parse(raw)
    return cleanRefinerySettings({
      skill_reprocess: parsed.skill_reprocess,
      skill_reprocess_efficiency: parsed.skill_reprocess_efficiency,
      implant_pct: parsed.implant_pct,
      station_bonus_pct: parsed.station_bonus_pct,
      refining_tax_pct: parsed.refining_tax_pct,
    })
  } catch {
    return DEFAULT_REFINERY_SETTINGS
  }
}

/**
 * Versión pura de la limpieza — usada por load y save. Devuelve un objeto
 * siempre válido, descartando cualquier valor fuera de los enums.
 */
export function cleanRefinerySettings(raw: Partial<RefinerySettings>): RefinerySettings {
  return {
    skill_reprocess: clampSkillLevel(raw.skill_reprocess),
    skill_reprocess_efficiency: clampSkillLevel(raw.skill_reprocess_efficiency),
    implant_pct: (clampEnum(raw.implant_pct, [0, 4, 6, 10]) as ImplantPct),
    station_bonus_pct: (clampEnum(raw.station_bonus_pct, [0, 1, 3, 6]) as StationBonusPct),
    refining_tax_pct: (clampEnum(raw.refining_tax_pct, [0, 2, 5]) as RefiningTaxPct),
  }
}

export function saveRefinerySettings(s: RefinerySettings, ts = Date.now()): void {
  if (typeof localStorage === 'undefined') return
  const clean = cleanRefinerySettings(s)
  localStorage.setItem(KEY, JSON.stringify({ ...clean, _updated_at: ts }))
}

export function refinerySettingsUpdatedAt(): number | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    return (JSON.parse(raw) as { _updated_at?: number })._updated_at ?? null
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Helpers internos
// ---------------------------------------------------------------------------

function clampSkillLevel(n: unknown): 0 | 1 | 2 | 3 | 4 | 5 {
  const x = typeof n === 'number' && Number.isFinite(n) ? Math.floor(n) : 0
  const clamped = Math.max(0, Math.min(5, x))
  return clamped as 0 | 1 | 2 | 3 | 4 | 5
}

function clampEnum<T extends number>(n: unknown, allowed: readonly T[]): T {
  if (typeof n === 'number' && (allowed as readonly number[]).includes(n)) {
    return n as T
  }
  // Fallback al primer valor permitido (típicamente 0).
  return allowed[0] as T
}

/**
 * Yield efectivo para mostrar en la UI (sin pegarle al backend). Útil
 * para que el "XX% yield" en el panel de settings reaccione instantáneo
 * a los clicks de los botones sin esperar round-trip.
 *
 * Misma fórmula que apps/api/src/reprocess.ts:computeFinalYield().
 */
export function computeYieldPct(s: RefinerySettings): number {
  const baseYield = 0.50
  const totalBonus =
    s.skill_reprocess * 0.02 +
    s.skill_reprocess_efficiency * 0.03 +
    s.implant_pct / 100 +
    s.station_bonus_pct / 100
  return Math.round(Math.min(0.85, baseYield + totalBonus) * 1000) / 10
}
